import { Edit } from 'ra-ui-materialui';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/styles';
import { useNotify, useRefresh } from 'react-admin';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import FormTitleBar from '../../core/components/FormTitleBar';
import EditToolbar from '../../core/components/edit/EditToolbar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import { getInitials, removePathEnd } from '../../core/utils';

import FundInvestorTagApi from '../../core/apis/FundInvestorTagApi';
import FundInvestorTagSummary from './FundInvestorTagSummary';
import FundInvestorTagsAssociationsApi from '../../core/apis/FundInvestorTagsAssociationsApi';
import { getIcError } from '../../core/providers/utils';

const styles = () => ({
  noActions: { marginTop: '0px' }
});

const FundInvestorTagTitle = ({ record }) => {
  return (
    <FormTitleBar
      showAvatar
      name={record.name}
      avatarContent={getInitials(record.name)}
    />
  );
};

const FundInvestorTagForm = props => {
  const {
    record,
    history,
    basePath,
    resource,
    displayToolbar,
    hasEditPermission,
    hasDeletePermission,
    setWhenBlockNavigation,
    ...rest
  } = props;

  const formRef = useRef();
  const initialValuesRef = useRef();
  const notify = useNotify();
  const refresh = useRefresh();

  const [ids, setIds] = useState([]);
  const [fundInvestors, setFundInvestors] = useState({});
  const [numberOfTotalRow, setNumberOfTotalRow] = useState(0);

  useEffect(() => {
    setIds(record.fundInvestors.map(fi => `${fi.fundId}:${fi.investorId}`));
    setFundInvestors(record.fundInvestors);
    setNumberOfTotalRow(record.fundInvestors.length);
  }, [record.fundInvestors]);

  const onBeforeDelete = () => {
    // Unblock navigation before deleting
    setWhenBlockNavigation(false);
  };

  const handleFormIsDirty = useCallback(
    dirty => {
      // Block navigation when the form is dirty
      setWhenBlockNavigation(dirty);
    },
    [setWhenBlockNavigation]
  );

  const handleNewFundInv = useCallback(
    newData => {
      if (newData.length > 0) {
        const parsedNewFundInv = newData.map(item => {
          return {
            fundId: item.fundId,
            fundName: item.fundName,
            investorId: item.investorId,
            investorName: item.investorName
          };
        });
        const newFundInvestors = record.fundInvestors.concat(parsedNewFundInv);
        setNumberOfTotalRow(newFundInvestors.length);
        setFundInvestors(newFundInvestors);
      } else {
        setNumberOfTotalRow(record.fundInvestors.length);
        setFundInvestors(record.fundInvestors);
      }
    },
    [record.fundInvestors]
  );

  const formReset = useCallback(() => {
    refresh();
    const initialValues = initialValuesRef.current;
    Object.keys(initialValues).forEach(key => {
      formRef.current.change(key, initialValues[key]);
    });
  }, [formRef, refresh]);

  const onSave = async formValues => {
    setWhenBlockNavigation(false);
    let hasModification = false;
    let hasError = false;

    /**
     * PUT v1/fundinvestortag
     */
    if (
      record.name !== formValues.name ||
      record.description !== formValues.description ||
      record.tagPrivacy !== formValues.tagPrivacy
    ) {
      hasModification = true;
      let payloadTag = {
        name: formValues.name,
        description: formValues.description,
        tagPrivacy: formValues.tagPrivacy
      };
      await FundInvestorTagApi.update(payloadTag, record.id).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'Fund-Investor Tag name has not been updated. ' + getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    const initialIds = record.fundInvestors.map(
      fi => `${fi.fundId}:${fi.investorId}`
    );
    const formIds = formValues.ids;

    /**
     * POST v1/fundinvestortags/associations
     */
    let payloadTagAssociationToAdd = [];
    const associationsIdsToAdd = formIds.filter(id => !initialIds.includes(id));
    associationsIdsToAdd
      .map(relation => relation.split(':'))
      .forEach(relation => {
        payloadTagAssociationToAdd.push({
          fundInvestorTagId: record.id,
          fundInvestor: {
            fundId: relation[0],
            investorId: relation[1]
          }
        });
      });

    if (payloadTagAssociationToAdd.length > 0) {
      hasModification = true;
      await FundInvestorTagsAssociationsApi.create(
        payloadTagAssociationToAdd
      ).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'New Fund Investor Tag associations have not been created. ' +
              getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    /**
     * DELETE v1/fundinvestortags/associations
     */
    let payloadTagAssociationToDelete = [];
    const associationsIdsToDelete = initialIds.filter(
      id => !formIds.includes(id)
    );
    associationsIdsToDelete
      .map(relation => relation.split(':'))
      .forEach(relation => {
        payloadTagAssociationToDelete.push({
          fundInvestorTagId: record.id,
          fundInvestor: {
            fundId: relation[0],
            investorId: relation[1]
          }
        });
      });
    if (payloadTagAssociationToDelete.length > 0) {
      hasModification = true;
      await FundInvestorTagsAssociationsApi.delete(
        payloadTagAssociationToDelete
      ).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'New Fund Investor Tag associations have not been deleted. ' +
              getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    if (!hasError && hasModification) {
      notify('ra.notification.custom_updated_message');
      formReset();
    } else {
      formReset();
    }
  };

  return (
    <CustomSimpleForm
      save={onSave}
      margin="normal"
      variant="standard"
      resource={resource}
      basePath={basePath}
      initialValues={{ ids }}
      toolbar={
        displayToolbar ? (
          <EditToolbar
            history={history}
            onBeforeDelete={onBeforeDelete}
            hasEditPermission={hasEditPermission}
            hasDeletePermission={hasDeletePermission}
            deleteContent="ra.message.confirm_delete_tag"
          />
        ) : (
          <></>
        )
      }
    >
      <FormSpy>
        {formProps => {
          formRef.current = formProps.form;
          initialValuesRef.current = formProps.initialValues;
          return (
            <FundInvestorTagSummary
              {...rest}
              record={record}
              fundInvestors={fundInvestors}
              numberOfTotalRow={numberOfTotalRow}
              handleNewFundInv={handleNewFundInv}
              hasEditPermission={hasEditPermission}
            />
          );
        }}
      </FormSpy>
      <FormSpy
        subscription={{ dirty: true }}
        onChange={e => handleFormIsDirty(e.dirty)}
      />
    </CustomSimpleForm>
  );
};

const FundInvestorTagEdit = props => {
  const {
    classes,
    userProfile,
    hasReadPermission,
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;

  const displayToolbar = props.match.isExact;
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onClose={onCancel}
            onConfirm={onConfirm}
          />
        )}
      </NavigationPrompt>
      <Edit
        {...rest}
        undoable={false}
        title={<FundInvestorTagTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <FundInvestorTagForm
          {...rest}
          displayToolbar={displayToolbar}
          hasEditPermission={hasEditPermission}
          hasDeletePermission={hasDeletePermission}
          setWhenBlockNavigation={setWhenBlockNavigation}
        />
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(FundInvestorTagEdit);
